import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["onClick"];
import React, { Fragment, useCallback } from 'react';
import { useIntl } from 'react-intl-next';
import Button from '@atlaskit/button';
import ThumbUpIcon from '@atlaskit/icon/core/thumbs-up';
import { ThumbUpIcon as LegacyThumbUpIcon } from '@atlaskit/legacy-custom-icons';
import { fg } from '@atlaskit/platform-feature-flags';
import { N500 } from '@atlaskit/theme/colors';
import Tooltip from '@atlaskit/tooltip';
import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';
import { useAIAnswerContext } from '../../../ai-answer-dialog/ai-answer-context';
import { SainAction, useSAINUsageCriteriaMet } from '../../../common/controller/analytics';
import { onDislikeButtonClick, onLikeButtonClick } from '../analytics';
import { messages } from './messages';
import { FeedbackButtonIconWrapper, Rotate180IconWrapper } from './styled';
var FeedbackButton = function FeedbackButton(_ref) {
  var active = _ref.active,
    isDisabled = _ref.isDisabled,
    onClickHandler = _ref.onClick,
    tooltipContent = _ref.tooltipContent,
    testId = _ref.testId,
    icon = _ref.icon;
  return /*#__PURE__*/React.createElement(Tooltip, {
    position: 'top',
    content: tooltipContent
  }, function (_ref2) {
    var onClick = _ref2.onClick,
      tooltipProps = _objectWithoutProperties(_ref2, _excluded);
    return /*#__PURE__*/React.createElement(Button, _extends({
      "aria-label": tooltipContent,
      spacing: 'none',
      appearance: isDisabled ? 'subtle-link' : 'subtle',
      iconBefore: /*#__PURE__*/React.createElement(FeedbackButtonIconWrapper, _extends({
        active: active
        // eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
        ,
        style: {
          cursor: 'pointer'
        }
      }, testId && {
        'data-testid': testId
      }, {
        isDisabled: isDisabled || false
      }), icon)
    }, !isDisabled && {
      onClick: onClickHandler
    }, tooltipProps));
  });
};
export var FeedbackButtons = function FeedbackButtons(_ref3) {
  var setShowFeedbackOptionsModal = _ref3.setShowFeedbackOptionsModal,
    setDisplayFlag = _ref3.setDisplayFlag;
  var _useIntl = useIntl(),
    formatMessage = _useIntl.formatMessage;
  var _useAIAnswerContext = useAIAnswerContext(),
    commonAttributes = _useAIAnswerContext.commonAttributes,
    fireAnalyticsEvent = _useAIAnswerContext.fireAnalyticsEvent,
    updateContextCache = _useAIAnswerContext.updateContextCache,
    liked = _useAIAnswerContext.liked,
    setLiked = _useAIAnswerContext.setLiked,
    likesDisabled = _useAIAnswerContext.likesDisabled,
    setLikesDisabled = _useAIAnswerContext.setLikesDisabled;
  var _useSAINUsageCriteria = useSAINUsageCriteriaMet(),
    fireSAINUsageEvent = _useSAINUsageCriteria.fireSAINUsageEvent;
  var _useAIEventsInstrumen = useAIEventsInstrumentation(),
    trackAIFeedbackSubmit = _useAIEventsInstrumen.trackAIFeedbackSubmit;
  // Using a single state to represent both like/dislike states
  // true - liked, false - disliked, undefined - neither are selected
  var likeButtonCallback = useCallback(function (e) {
    e.preventDefault();
    setDisplayFlag(true);
    setLiked(true);
    setLikesDisabled(true);
    updateContextCache({
      collapsed: false,
      liked: true
    });
    fireAnalyticsEvent(onLikeButtonClick(commonAttributes));
    fg('kd_enable_new_usage_tracking') ? fireSAINUsageEvent({
      metCriteria: SainAction.POSITIVE_FEEDBACK_PROVIDED,
      commonAttributes: commonAttributes
    }) : trackAIFeedbackSubmit('up');
  }, [setLiked, setLikesDisabled, fireAnalyticsEvent, commonAttributes, updateContextCache, setDisplayFlag, fireSAINUsageEvent, trackAIFeedbackSubmit]);
  var dislikeButtonCallback = useCallback(function (e) {
    e.preventDefault();
    setShowFeedbackOptionsModal(true);
    setLiked(false);
    setLikesDisabled(true);
    updateContextCache({
      collapsed: false,
      liked: false
    });
    fireAnalyticsEvent(onDislikeButtonClick(commonAttributes));
    fg('kd_enable_new_usage_tracking') ? fireSAINUsageEvent({
      metCriteria: SainAction.NEGATIVE_FEEDBACK_PROVIDED,
      commonAttributes: commonAttributes
    }) : trackAIFeedbackSubmit('down');
  }, [setLiked, setLikesDisabled, fireAnalyticsEvent, commonAttributes, updateContextCache, setShowFeedbackOptionsModal, fireSAINUsageEvent, trackAIFeedbackSubmit]);
  return /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement(FeedbackButton, {
    active: liked === true,
    onClick: likeButtonCallback,
    tooltipContent: formatMessage(messages.definition_footer_like_button_tooltip),
    icon: /*#__PURE__*/React.createElement(ThumbUpIcon, {
      label: "",
      spacing: "spacious",
      color: "var(--ds-icon, ".concat(N500, ")"),
      LEGACY_fallbackIcon: LegacyThumbUpIcon
    }),
    isDisabled: liked && likesDisabled,
    testId: "definition-footer-like-button"
  }), /*#__PURE__*/React.createElement(FeedbackButton, {
    active: liked === false,
    onClick: dislikeButtonCallback,
    tooltipContent: formatMessage(messages.definition_footer_dislike_button_tooltip),
    icon: /*#__PURE__*/React.createElement(Rotate180IconWrapper, null, /*#__PURE__*/React.createElement(ThumbUpIcon, {
      label: "",
      spacing: "spacious",
      color: "var(--ds-icon, ".concat(N500, ")"),
      LEGACY_fallbackIcon: LegacyThumbUpIcon
    })),
    isDisabled: !liked && likesDisabled,
    testId: "definition-footer-dislike-button"
  }));
};